import { Injectable } from '@angular/core';
import { ResourceResponse } from '../../../../models';

@Injectable({ providedIn: 'root' })
export class SkillResourcesCacheService {
  private activeSlug = '';
  private readonly resourcesCache = new Map<string, ResourceResponse[]>();
  private readonly resourcesLoading = new Set<string>();
  private readonly resourcesExpanded = new Set<string>();
  private readonly topicsExpanded = new Set<string>();

  setActiveSlug(slug: string): void {
    if (this.activeSlug === slug) {
      return;
    }
    this.activeSlug = slug;
    this.clearAll();
  }

  clearAll(): void {
    this.resourcesCache.clear();
    this.resourcesLoading.clear();
    this.resourcesExpanded.clear();
    this.topicsExpanded.clear();
  }

  clearForSkill(skillId: string): void {
    const prefix = `${skillId}|`;
    for (const key of [...this.resourcesCache.keys()]) {
      if (key === skillId || key.startsWith(prefix)) {
        this.resourcesCache.delete(key);
        this.resourcesExpanded.delete(key);
        this.resourcesLoading.delete(key);
      }
    }
    for (const key of [...this.topicsExpanded.keys()]) {
      if (key === skillId || key.startsWith(prefix)) {
        this.topicsExpanded.delete(key);
      }
    }
  }

  clearResourceKey(skillId: string, topicName?: string): void {
    const key = this.resourceCacheKey(skillId, topicName);
    this.resourcesCache.delete(key);
    this.resourcesExpanded.delete(key);
    this.resourcesLoading.delete(key);
  }

  resourceCacheKey(skillId: string, topicName?: string): string {
    return topicName?.trim() ? `${skillId}|${topicName.trim()}` : skillId;
  }

  isTopicExpanded(skillId: string, topicName: string): boolean {
    return this.topicsExpanded.has(this.resourceCacheKey(skillId, topicName));
  }

  toggleTopic(skillId: string, topicName: string): void {
    const key = this.resourceCacheKey(skillId, topicName);
    if (this.topicsExpanded.has(key)) {
      this.topicsExpanded.delete(key);
      return;
    }
    this.topicsExpanded.add(key);
  }

  isResourcesExpanded(skillId: string, topicName?: string): boolean {
    return this.resourcesExpanded.has(this.resourceCacheKey(skillId, topicName));
  }

  isResourcesLoading(skillId: string, topicName?: string): boolean {
    return this.resourcesLoading.has(this.resourceCacheKey(skillId, topicName));
  }

  getCachedResources(skillId: string, topicName?: string): ResourceResponse[] {
    return this.resourcesCache.get(this.resourceCacheKey(skillId, topicName)) ?? [];
  }

  markResourcesExpanded(skillId: string, topicName?: string): void {
    this.resourcesExpanded.add(this.resourceCacheKey(skillId, topicName));
  }

  collapseResources(skillId: string, topicName?: string): void {
    this.resourcesExpanded.delete(this.resourceCacheKey(skillId, topicName));
  }

  beginResourcesLoad(skillId: string, topicName?: string): boolean {
    const key = this.resourceCacheKey(skillId, topicName);
    if (this.resourcesCache.has(key) || this.resourcesLoading.has(key)) {
      return false;
    }
    this.resourcesLoading.add(key);
    return true;
  }

  completeResourcesLoad(skillId: string, topicName: string | undefined, resources: ResourceResponse[]): void {
    const key = this.resourceCacheKey(skillId, topicName);
    this.resourcesCache.set(key, resources);
    this.resourcesLoading.delete(key);
  }

  failResourcesLoad(skillId: string, topicName?: string): void {
    const key = this.resourceCacheKey(skillId, topicName);
    this.resourcesExpanded.delete(key);
    this.resourcesLoading.delete(key);
  }
}